// @flow
import { injectIntl, intlShape } from 'react-intl';
import React, { Component } from 'react';
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft';
import Facet from './Facet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCategoryFacetLink } from '../../utils/urls';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { modifyParamsRemovePage } from '../../utils/queryParams';
import ScrollBar from '../../components/ScrollBar/ScrollBar';
import styles from './TaxonomyFacet.module.scss';
import TaxonomyNode from './TaxonomyNode';
import TaxonomyParent from './TaxonomyParent';

const MAX_HEIGHT_FOR_CHECKS = 600;

type Props = {
    category: any,
    facetId: string,
    intl: intlShape,
    landingPageUrl: string,
    loadPageWithFacetCollapsed?: boolean,
    queryParams: any,
    showLandingPageLinks: boolean,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
    taxonomySelected: Function,
    title: string,
};

type State = {
    seeMore: boolean,
};

class TaxonomyFacet extends Component<Props, State> {
    static defaultProps = {
        loadPageWithFacetCollapsed: false,
    };

    state = { seeMore: false };

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.category, prevProps.category)) {
            this.setSeeMoreToFalse();
        }
    }

    onSeeMore = () => {
        const seeMore = !this.state.seeMore;
        this.setState({ seeMore });
    };

    setSeeMoreToFalse = () => {
        this.setState({ seeMore: false });
    };

    parentSelected = (parentId, categoryId) => {
        this.setSeeMoreToFalse();
        this.props.taxonomyParentSelected(parentId, categoryId, this.props.facetId);
    };

    taxonomySelected = id => {
        this.setSeeMoreToFalse();
        this.props.taxonomySelected(this.props.facetId, id);
    };

    taxonomyAllSelected = categorySelectedID => {
        this.setSeeMoreToFalse();
        this.props.taxonomyAllSelected(this.props.facetId, categorySelectedID);
    };

    createAllCategoriesUrl = url => {
        if (url !== '' && url !== undefined) {
            return `/c/${url}/`;
        }
        return '/c/';
    };

    getSelectedCategoryId = facets => {
        if (facets && facets.length > 0 && facets[0].selected) {
            return facets[0].id;
        }
        return 0;
    };

    render() {
        const {
            category,
            showLandingPageLinks,
            title = '',
            landingPageUrl,
            loadPageWithFacetCollapsed,
            intl,
        } = this.props;

        const { formatMessage } = intl;

        let { queryParams } = this.props;
        queryParams = modifyParamsRemovePage(queryParams);
        const seeMore = this.state.seeMore;
        const categoryIdSelected = this.getSelectedCategoryId(category.facets);

        if (category.facets) {
            const showAllLink =
                showLandingPageLinks && category.facets.some(facet => facet.landingPageUrl) ? (
                    <Link className={styles.link} route to={getCategoryFacetLink(landingPageUrl, queryParams)}>
                        {' '}
                        <FontAwesomeIcon className={styles.icon} icon={faAngleLeft} /> {formatMessage({ id: 'all' })}{' '}
                        {title}{' '}
                    </Link>
                ) : (
                    <a onClick={() => this.taxonomyAllSelected(categoryIdSelected)}>
                        <FontAwesomeIcon className={styles.icon} icon={faAngleLeft} /> {formatMessage({ id: 'all' })}{' '}
                        {title}{' '}
                    </a>
                );

            const parents = (
                <TaxonomyParent
                    queryParams={queryParams}
                    parents={category.facets[0].parents}
                    categoryID={category.facets[0].id}
                    parentSelected={this.parentSelected}
                    showLandingPageLinks={showLandingPageLinks}
                />
            );
            const children = (
                <TaxonomyNode
                    facets={category.facets}
                    queryParams={queryParams}
                    onSeeMore={this.onSeeMore}
                    renderAsParent={category.facets.length > 1}
                    seeMore={seeMore}
                    showLandingPageLinks={showLandingPageLinks}
                    taxonomySelected={this.taxonomySelected}
                />
            );
            const hideTitle = title === 'Categories';
            const hideExpandCollapse = hideTitle;
            const facetClass = title
                .trim()
                .replace('/', '')
                .replace(/\s+/g, '-')
                .toLowerCase();

            return (
                <Facet
                    className={`taxonomy-facet ${facetClass}`}
                    title={title}
                    hideTitle={hideTitle}
                    hideExpandCollapse={hideExpandCollapse}
                    closeFacet={loadPageWithFacetCollapsed}
                >
                    <ScrollBar maxHeight={MAX_HEIGHT_FOR_CHECKS} showScrollBar={seeMore}>
                        <div className={styles['taxonomy-container']}>
                            {categoryIdSelected > 0 && showAllLink}
                            {parents}
                            {children}
                        </div>
                    </ScrollBar>
                </Facet>
            );
        }
        return null;
    }
}

export default injectIntl(TaxonomyFacet);
